// LeadCommandCenterHeader — Phase 1 mount of the AI Lead Command Center.
//
// Renders three things at the very top of LeadDetailDrawer:
//   1. Header stats row  — AI score 0-100 · Hot/Warm/Cold · Expected
//                          commission · Probability of close · Next-best-action
//   2. AI Briefing card  — natural language 2-4 sentences synthesizing
//                          who the lead is, what they want, and what
//                          matters going in
//   3. AI Strategy card  — opening line + focus / avoid / objections
//
// Data comes from POST /api/leads/command-center — deterministic
// heuristic in Phase 1; Phase 6 (Digital Twin) upgrades briefing +
// strategy to LLM-synthesized output.
//
// Fail-soft: if the endpoint 500s the header hides itself. Never
// renders a plausible-with-no-source number.

import React from 'react';
import { readPersistedJwtDirect } from './authedFetch';
import { supabase } from '../lib/supabase';

async function bearer() {
  const token = readPersistedJwtDirect();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmt$(cents) {
  if (!Number.isFinite(cents) || cents === 0) return '$0';
  return `$${(cents / 100).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

const HWC_STYLE = {
  hot:  { bg: '#fef2f2', border: '#fca5a5', text: '#b91c1c', label: '🔥 Hot' },
  warm: { bg: '#fef3c7', border: '#fcd34d', text: '#92400e', label: '☀️ Warm' },
  cold: { bg: '#f1f5f9', border: '#cbd5e1', text: '#475569', label: '❄️ Cold' },
};

function Tile({ label, value, tone, sub }) {
  return (
    <div style={{
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      padding: '8px 10px',
      flex: '1 1 100px',
      minWidth: 100,
    }}>
      <div style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#64748b', fontWeight: 700 }}>{label}</div>
      <div style={{
        fontSize: 15, fontWeight: 800, marginTop: 2,
        color: tone || '#0f172a', fontVariantNumeric: 'tabular-nums',
      }}>{value}</div>
      {sub && <div style={{ fontSize: 9, color: '#64748b', marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

export default function LeadCommandCenterHeader({ lead, callHistory = [], outreachLog = [] }) {
  const [state, setState] = React.useState({ loading: true, data: null, error: null });
  const [expanded, setExpanded] = React.useState(false);

  React.useEffect(() => {
    const controller = new AbortController();
    (async () => {
      const { authedFetchLead } = await import('./authedFetch');
      const res = await authedFetchLead(
        '/api/leads/command-center',
        { lead, callHistory, outreachLog },
        controller.signal
      );
      if (controller.signal.aborted) return;
      setState({ loading: false, data: res.ok ? res.data : null, error: res.ok ? null : res.error });
    })();
    return () => { controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id, callHistory.length, outreachLog.length]);

  // Loading — return null briefly. authedFetch.js caps the fetch at
  // 10s (with a 2s bearer-token watchdog inside), so this never
  // stays true forever anymore. The diag-tile from commit 22b5c95
  // was replaced with the real fix (authedFetch timeouts).
  if (state.loading) return null;
  // Error path was silently hiding the panel, which made real
  // endpoint failures indistinguishable from "empty data" on the
  // smoke test. Surface the failure so a broken deploy of the
  // command-center endpoint is visible instead of invisible.
  if (state.error || !state.data) {
    return (
      <div style={{
        background: '#fef9c3',
        border: '1px solid #fde047',
        borderRadius: 10,
        padding: 10,
        marginBottom: 8,
        fontSize: 12,
        color: '#854d0e',
      }}>
        <strong>AI briefing temporarily unavailable</strong>
        {state.error && (
          <span style={{ color: '#a16207', marginLeft: 6, fontSize: 11 }}>
            ({state.error})
          </span>
        )}
        <div style={{ fontSize: 10, color: '#a16207', marginTop: 3 }}>
          Score, expected commission and strategy will appear once the
          endpoint responds. All other panels below still work.
        </div>
      </div>
    );
  }

  const d = state.data;
  const hwc = HWC_STYLE[d.hot_warm_cold] || HWC_STYLE.warm;

  return (
    <div style={{
      background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
      border: '2px solid #cbd5e1',
      borderRadius: 12,
      padding: 12,
      marginBottom: 8,
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{
          fontSize: 9, letterSpacing: '0.24em', textTransform: 'uppercase',
          color: '#0891b2', fontWeight: 800,
        }}>AI Command Center</div>
        <div style={{
          background: hwc.bg, color: hwc.text, border: `1px solid ${hwc.border}`,
          padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 800,
        }}>{hwc.label}</div>
        <button
          onClick={() => setExpanded(v => !v)}
          style={{
            marginLeft: 'auto', background: 'transparent', border: 'none',
            color: '#0891b2', fontSize: 11, fontWeight: 700, cursor: 'pointer',
          }}
        >{expanded ? 'Collapse' : 'Show strategy'}</button>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        <Tile
          label="AI Score"
          value={`${d.ai_score_100}`}
          tone={d.ai_score_100 >= 75 ? '#059669' : d.ai_score_100 >= 50 ? '#d97706' : '#475569'}
        />
        <Tile
          label="Expected commission"
          value={fmt$(d.expected_commission_cents)}
          tone="#059669"
        />
        <Tile
          label="Close probability"
          value={`${d.probability_pct}%`}
          tone={d.probability_pct >= 70 ? '#059669' : d.probability_pct >= 40 ? '#d97706' : '#475569'}
        />
        <Tile
          label="Next best action"
          value={d.next_best_action.verb}
          sub={d.next_best_action.target}
        />
      </div>

      {/* Briefing */}
      <div style={{
        background: '#fefce8',
        border: '1px solid #fde047',
        borderRadius: 8,
        padding: '10px 12px',
        fontSize: 13, lineHeight: 1.55, color: '#3f3f46',
      }}>
        <div style={{ fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#a16207', fontWeight: 800, marginBottom: 4 }}>
          AI Briefing
        </div>
        {d.briefing}
        {d.next_best_action.reason && (
          <div style={{ fontSize: 11, color: '#78716c', marginTop: 6, fontStyle: 'italic' }}>
            NBA reason: {d.next_best_action.reason}
          </div>
        )}
      </div>

      {/* Expandable strategy */}
      {expanded && (
        <div style={{
          marginTop: 8,
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: 8,
          padding: '10px 12px',
        }}>
          <div style={{ fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#1d4ed8', fontWeight: 800, marginBottom: 4 }}>
            AI Strategy
          </div>
          <div style={{ fontSize: 12, color: '#1e40af', fontStyle: 'italic', marginBottom: 8 }}>
            Open with: {d.strategy.open}
          </div>
          <div style={{ display: 'grid', gap: 6, gridTemplateColumns: '1fr 1fr', fontSize: 11 }}>
            <div>
              <div style={{ fontWeight: 800, color: '#059669', marginBottom: 3 }}>✔ Focus</div>
              <ul style={{ margin: 0, paddingLeft: 16, color: '#334155', lineHeight: 1.6 }}>
                {d.strategy.focus.map(f => <li key={f}>{f}</li>)}
              </ul>
            </div>
            <div>
              <div style={{ fontWeight: 800, color: '#b91c1c', marginBottom: 3 }}>✘ Avoid</div>
              <ul style={{ margin: 0, paddingLeft: 16, color: '#334155', lineHeight: 1.6 }}>
                {d.strategy.avoid.map(a => <li key={a}>{a}</li>)}
              </ul>
            </div>
          </div>
          {d.strategy.objections?.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 11 }}>
              <div style={{ fontWeight: 800, color: '#1d4ed8', marginBottom: 4 }}>Likely objections + rebuttals</div>
              {d.strategy.objections.map((o, i) => (
                <div key={i} style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #dbeafe' }}>
                  <div style={{ color: '#475569' }}>{o.objection}</div>
                  <div style={{ color: '#1e40af', marginTop: 2, fontStyle: 'italic' }}>→ {o.rebuttal}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
